import { Dispatch, SetStateAction, memo } from 'react';
import { Grid, Text } from '@mantine/core';
import moment from 'moment';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import { formatMoneyString } from '../../../utils/strings';
import Table from '../../../components/Table';
import { buildOrderFolio } from '../../../utils/ordersUtils';
import { IOrder } from '../../../interfaces/Order';

export default memo(function TableView({
  tableInfo = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination,
  footerInfo = {
    totalAmountPaid: 0,
    totalFeeTotal: 0,
    totalNetAmount: 0
  }
}: {
  tableInfo: any[];
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  footerInfo: {
    totalAmountPaid: number;
    totalFeeTotal?: number;
    totalNetAmount?: number;
  };
}) {
  const { t } = useTranslation(['FieldName']);

  const fallbackTotals = tableInfo.reduce(
    (acc, row) => {
      const gross = Number(row?.paymentLines?.amountPaid ?? 0);
      const fee = Number(row?.paymentLines?.processingFee?.feeTotal ?? 0);
      const net = Number(row?.paymentLines?.processingFee?.netAmount ?? gross);

      acc.totalAmountPaid += gross;
      acc.totalFeeTotal += fee;
      acc.totalNetAmount += net;
      return acc;
    },
    { totalAmountPaid: 0, totalFeeTotal: 0, totalNetAmount: 0 }
  );

  const totalAmountPaid = Number(footerInfo?.totalAmountPaid ?? fallbackTotals.totalAmountPaid);
  const totalFeeTotal = Number(footerInfo?.totalFeeTotal ?? fallbackTotals.totalFeeTotal);
  const totalNetAmount = Number(footerInfo?.totalNetAmount ?? fallbackTotals.totalNetAmount);
  const columns = [
    {
      accessorFn: (row: any) => buildOrderFolio((row as unknown as IOrder)?.folioPrefix, (row as unknown as IOrder)?.sequence),
      header: t('FieldName:order')
    },
    {
      accessorFn: (row: any) => formatMoneyString(Number(row.orderTotal ?? 0).toFixed(2)),
      header: t('FieldName:orderCurrentTotal')
    },
    {
      accessorFn: (row: any) => formatMoneyString((row.paymentLines.amountPaid || 0).toFixed(2)),
      header: t('FieldName:paymentAmount'),
      Footer: () => {
        return (
          <Grid>
            <Grid.Col span={12}>
              <Text fw={'bold'}>{formatMoneyString(totalAmountPaid.toFixed(2))}</Text>
            </Grid.Col>
          </Grid>
        );
      }
    },
    {
      accessorFn: (row: any) => t(`FieldName:paymentMethods.${row.paymentLines.paymentMethod}`),
      header: t('FieldName:paymentMethod')
    },
    {
      accessorFn: (row: any) =>
        formatMoneyString(
          ((row?.paymentLines?.processingFee?.feeTotal ?? 0) as number).toFixed(2)
        ),
      header: t('FieldName:fee'),
      Footer: () => {
        return (
          <Grid>
            <Grid.Col span={12}>
              <Text fw={'bold'}>{formatMoneyString(totalFeeTotal.toFixed(2))}</Text>
            </Grid.Col>
          </Grid>
        );
      }
    },
    {
      accessorFn: (row: any) => {
        const gross = Number(row?.paymentLines?.amountPaid ?? 0);
        const net = Number(row?.paymentLines?.processingFee?.netAmount ?? gross);
        return formatMoneyString(net.toFixed(2));
      },
      header: t('FieldName:net'),
      Footer: () => {
        return (
          <Grid>
            <Grid.Col span={12}>
              <Text fw={'bold'}>{formatMoneyString(totalNetAmount.toFixed(2))}</Text>
            </Grid.Col>
          </Grid>
        );
      }
    },
    {
      accessorFn: (row: any) => moment(row.paymentLines.paymentDate).format('DD/MM/YYYY hh:mm'),
      header: t('FieldName:date')
    },
    {
      accessorFn: (row: any) => row.paymentLines?.cashierBox?.name,
      header: t('FieldName:cashierBox')
    },
    {
      accessorFn: (row: any) => row.paymentLines?.createdBy?.name,
      header: t('FieldName:createdBy')
    },
    {
      accessorFn: (row: any) =>
        row.paymentLines.isOnACashUpReport ? t('FieldName:yes') : t('FieldName:no'),
      header: t('FieldName:isOnACashUpReport')
    },
    {
      accessorFn: (row: any) =>
        row.customer.customerNumber,
      header: t('FieldName:customerNumber')
    },
    {
      accessorFn: (row: any) =>
        row.customer.name,
      header: t('FieldName:customer')
    }
  ];

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            pagination={pagination}
            setPagination={setPagination}
            tableData={tableInfo}
            isError={isError}
            paginationIsLoading={isLoading}
            numPages={pages}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{}}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
